// src/web/features/materials/components/FilterBar.tsx
import React, { useState, useEffect } from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Search, Filter, X } from 'lucide-react';
import { config } from '../../auth/config';

interface FilterBarProps {
  filters: {
    subject?: string;
    classSection?: string;
    search?: string;
  };
  setFilters: (filters: any) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, setFilters }) => {
  const [classes, setClasses] = useState<string[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);

  const subjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 
    'English', 'Hindi', 'Social Science', 'Computer Science'
  ];

  // Fetch classes from backend
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch(`${config.api.baseUrl}/api/materials/classes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setClasses(data.classes || []);
          console.log('📚 Classes loaded from backend:', data.classes);
        } else {
          console.error('Failed to fetch classes:', response.status);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        setLoadingClasses(false);
      }
    };

    fetchClasses();
  }, []);

  const handleFilterChange = (key: string, value: string) => {
    setFilters({
      ...filters,
      [key]: value === '' ? undefined : value
    });
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = filters.subject || filters.classSection || filters.search;

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-gray-600" />
        <h3 className="font-medium text-gray-700">Filter Materials</h3>
        {hasActiveFilters && (
          <Button
            onClick={clearFilters}
            size="sm"
            variant="outline"
            className="ml-auto text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search materials..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Subject Filter */}
        <select
          value={filters.subject || ''}
          onChange={(e) => handleFilterChange('subject', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Subjects</option>
          {subjects.map((subject) => (
            <option key={subject} value={subject}>
              {subject}
            </option>
          ))}
        </select>

        {/* Class Filter - Now from Backend */}
        <select
          value={filters.classSection || ''}
          onChange={(e) => handleFilterChange('classSection', e.target.value)}
          disabled={loadingClasses}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">
            {loadingClasses ? 'Loading classes...' : 'All Classes'}
          </option>
          {classes.map((className) => (
            <option key={className} value={className}>
              {className}
            </option>
          ))}
        </select>
        {loadingClasses && (
          <p className="text-xs text-blue-600 flex items-center gap-1 col-span-full">
            <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
            Loading available classes from server...
          </p>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <span className="text-xs text-gray-600">Active filters:</span>
          {filters.subject && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
              Subject: {filters.subject}
              <button onClick={() => handleFilterChange('subject', '')}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.classSection && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
              Class: {filters.classSection}
              <button onClick={() => handleFilterChange('classSection', '')}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
              Search: "{filters.search}"
              <button onClick={() => handleFilterChange('search', '')}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export { FilterBar };
